import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { api, useAuth, withRefresh } from "@/lib/auth";
import { chimeOn, playBell } from "@/lib/chime";
import { ChimeToggle } from "./ChimeToggle";
import { Sigil } from "./Sigil";

const ease = [0.22, 1, 0.36, 1];
const pad = (n) => String(n).padStart(2, "0");

const Half = ({ side }) => (
  <motion.div
    className="absolute inset-0"
    style={{ clipPath: side === "l" ? "polygon(0 0, 52% 0, 46% 30%, 54% 55%, 47% 78%, 52% 100%, 0 100%)" : "polygon(52% 0, 100% 0, 100% 100%, 52% 100%, 47% 78%, 54% 55%, 46% 30%)" }}
    initial={{ x: 0, rotate: 0, opacity: 1 }}
    animate={{ x: side === "l" ? -140 : 140, rotate: side === "l" ? -24 : 24, opacity: 0 }}
    transition={{ delay: 1.05, duration: 1.1, ease }}
  >
    <div className="absolute inset-0 rounded-full bg-[radial-gradient(circle_at_35%_30%,#F4D9A4,#D9B47A_45%,#8A6630_100%)] shadow-[0_0_80px_rgba(217,180,122,0.45),inset_0_-10px_30px_rgba(90,60,20,0.6)]" />
    <div className="absolute inset-[10%] rounded-full border border-[#5A3F18]/40" />
    <Sigil className="absolute inset-[24%] w-[52%] h-[52%]" stroke="#3A2810" width={3.5} />
  </motion.div>
);

const Ceremony = ({ i, name, onDone }) => {
  useEffect(() => {
    if (!chimeOn()) return undefined;
    const t = setTimeout(() => playBell(), 1000);
    return () => clearTimeout(t);
  }, []);
  return (
  <motion.div
    data-testid="gate-unlock-ceremony"
    role="dialog"
    aria-label={`Gate ${pad(i + 1)} unsealed`}
    onClick={onDone}
    className="fixed inset-0 z-[90] flex flex-col items-center justify-center bg-[#0A0706]/95 backdrop-blur-md cursor-pointer px-6 text-center"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0, transition: { duration: 0.6 } }}
  >
    <motion.div
      className="absolute w-[70vmin] h-[70vmin] rounded-full bg-[radial-gradient(circle,rgba(217,180,122,0.5),transparent_65%)]"
      initial={{ scale: 0.2, opacity: 0 }}
      animate={{ scale: [0.2, 0.2, 1.6], opacity: [0, 0, 1, 0] }}
      transition={{ duration: 2.2, times: [0, 0.45, 0.75, 1] }}
    />
    <motion.div
      className="relative w-40 h-40 sm:w-52 sm:h-52"
      initial={{ scale: 0.6, opacity: 0 }}
      animate={{ scale: [0.6, 1, 1, 1.04], opacity: 1, rotate: [0, 0, -3, 3, 0] }}
      transition={{ duration: 1.05, ease }}
    >
      <Half side="l" />
      <Half side="r" />
      <motion.span
        className="absolute left-1/2 top-0 bottom-0 w-px -ml-px bg-[#FFF3D6]"
        initial={{ scaleY: 0, opacity: 0 }}
        animate={{ scaleY: [0, 1, 1], opacity: [0, 1, 0] }}
        transition={{ delay: 0.75, duration: 0.7 }}
      />
    </motion.div>
    <motion.p
      className="relative mt-10 font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.3, duration: 0.8 }}
    >
      Gate {pad(i + 1)} · Unsealed
    </motion.p>
    <motion.p
      data-testid="gate-unlock-name"
      className="relative mt-4 font-display italic text-4xl sm:text-6xl text-[#F2E8DF]"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 1.5, duration: 0.9, ease }}
    >
      {name}
    </motion.p>
    <p className="absolute bottom-8 text-[12px] text-[#A8998C]">Tap to enter</p>
    <ChimeToggle className="absolute top-6 right-6" />
  </motion.div>
  );
};

export default function GateUnlock({ i, name }) {
  const { user } = useAuth();
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (!user?.initiate) return undefined;
    let alive = true;
    withRefresh(() => api.post(`/gates/${i}/open`))
      .then(({ data }) => alive && data?.first && setShow(true))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [i, user?.initiate]);
  useEffect(() => {
    if (!show) return undefined;
    const t = setTimeout(() => setShow(false), 3400);
    return () => clearTimeout(t);
  }, [show]);
  return <AnimatePresence>{show && <Ceremony key={i} i={i} name={name} onDone={() => setShow(false)} />}</AnimatePresence>;
}
